/**
 * Arches — free-standing alley arches, colonnades and vaulted passages.
 * owner: level
 */
import { archwayPanel, chamferBox, xform } from '../Geo.js';

/**
 * Free-standing arch spanning a street. Local +X is across the span,
 * local +Z is the street direction (thickness).
 */
export function archway(w, {
  x, z, y = 0, rotY = 0, span = 5.0, pierHeight = 2.6, thickness = 0.9,
  jamb = 0.7, mat = 'plaster_wall', surface = 'concrete', capital = true,
  keystone = true, damage = true,
}) {
  const g = archwayPanel({ span, pierHeight, thickness, jamb, bevel: 0.05 });
  w.batch.add(g, mat, { matrix: xform([x, y, z], rotY), surface, uvScale: 1.5 });
  g.dispose();

  const r = span / 2;
  const total = pierHeight + r + 0.35;
  const outerW = span + jamb * 2;

  // impost blocks where the arch springs
  if (capital) {
    for (const s of [-1, 1]) {
      const c = chamferBox(jamb + 0.24, 0.21, thickness + 0.22, 0.036);
      const px = x + Math.cos(rotY) * (s * (r + jamb / 2));
      const pz = z - Math.sin(rotY) * (s * (r + jamb / 2));
      w.batch.add(c, 'concrete_wall', {
        matrix: xform([px, y + pierHeight - 0.1, pz], rotY), surface: 'concrete', uvScale: 0.7,
      });
      c.dispose();
    }
  }
  if (keystone) {
    const k = chamferBox(0.34, 0.46, thickness + 0.16, 0.04);
    w.batch.add(k, 'concrete_wall', {
      matrix: xform([x, y + pierHeight + r + 0.02, z], rotY), surface: 'concrete', uvScale: 0.6,
    });
    k.dispose();
  }
  // coping across the top
  const cap = chamferBox(outerW + 0.2, 0.17, thickness + 0.26, 0.04);
  w.batch.add(cap, 'concrete_wall', {
    matrix: xform([x, y + total + 0.06, z], rotY), surface: 'concrete', uvScale: 0.7,
  });
  cap.dispose();

  if (damage) {
    const rng = w.rng;
    for (let i = 0; i < 2; i++) {
      const s = rng.sign();
      const dw = rng.range(0.4, 0.9), dh = rng.range(0.4, 1.0);
      const px = x + Math.cos(rotY) * (s * (r + jamb * 0.5));
      const pz = z - Math.sin(rotY) * (s * (r + jamb * 0.5));
      const b = chamferBox(dw, dh, 0.09, 0.03);
      // exposed blockwork core, matching Walls.damagePatch — not fired brick
      w.batch.add(b, 'concrete_wall', {
        matrix: xform([px, y + rng.range(0.4, pierHeight - 0.6), pz + (thickness / 2 - 0.03) * rng.sign()], rotY),
        surface: 'concrete', uvScale: 0.5, tint: [0.72, 0.66, 0.58],
      });
      b.dispose();
    }
  }

  // collision: piers + the spandrel above the arch
  for (const s of [-1, 1]) {
    const px = x + Math.cos(rotY) * (s * (r + jamb / 2));
    const pz = z - Math.sin(rotY) * (s * (r + jamb / 2));
    w.blocker(px, y + pierHeight / 2, pz, jamb, pierHeight, thickness, rotY, surface);
  }
  w.blocker(x, y + pierHeight + r + 0.2, z, outerW, 0.7, thickness, rotY, surface, false);
}

/** A run of arches on piers — a shaded souk colonnade. */
export function colonnade(w, {
  x, z, rotY = 0, bays = 4, bay = 3.2, pierW = 0.6, pierD = 0.7,
  height = 3.0, mat = 'plaster_wall', surface = 'concrete', roof = true,
}) {
  const total = bays * bay;
  const cos = Math.cos(rotY), sin = Math.sin(rotY);
  const at = (u, o = 0) => [x + cos * u + sin * o, z - sin * u + cos * o];
  for (let i = 0; i <= bays; i++) {
    const u = -total / 2 + i * bay;
    const p = at(u);
    const g = chamferBox(pierW, height, pierD, 0.045);
    w.batch.add(g, mat, { matrix: xform([p[0], height / 2, p[1]], rotY), surface, uvScale: 1.2 });
    g.dispose();
    const cap = chamferBox(pierW + 0.2, 0.17, pierD + 0.2, 0.034);
    w.batch.add(cap, 'concrete_wall', { matrix: xform([p[0], height + 0.08, p[1]], rotY), surface: 'concrete', uvScale: 0.6 });
    cap.dispose();
    w.blocker(p[0], height / 2, p[1], pierW, height, pierD, rotY, surface);
  }
  if (roof) {
    const g = chamferBox(total + pierW + 0.5, 0.30, pierD + 0.7, 0.045);
    w.batch.add(g, 'concrete_floor', { matrix: xform([x, height + 0.31, z], rotY), surface: 'concrete', uvScale: 1.6 });
    g.dispose();
    w.blocker(x, height + 0.31, z, total + pierW + 0.5, 0.3, pierD + 0.7, rotY, 'concrete', false);
    // beam ends poking out — silhouette
    for (let i = 0; i < bays * 2 + 1; i++) {
      const u = -total / 2 + (i * total) / (bays * 2);
      const p = at(u, pierD / 2 + 0.42);
      const bm = chamferBox(0.13, 0.15, 0.5, 0.024);
      w.batch.add(bm, 'wood_plank', { matrix: xform([p[0], height + 0.16, p[1]], rotY), surface: 'wood', uvScale: 0.4 });
      bm.dispose();
    }
  }
}
